import firebase from "firebase/app";
import "firebase/auth";
import "firebase/database";
import { fromRef, ListenEvent } from "rxfire/database";
import { from, Observable, of } from "rxjs";
import { debounceTime, flatMap, map } from "rxjs/operators";

import { AuthService } from "@/services/AuthService";
import { CandidateRanking } from "@/services/DTOs/CandidateRanking";
import { IPeopleStats } from "@/services/DTOs/firebase/PeopleStats";
import { UserService } from "@/services/UserService";

const config = {
  apiKey: process.env.VUE_APP_DISCOVERY_UI_GOOGLE_SERVICES_API_KEY,
  authDomain: process.env.VUE_APP_DISCOVERY_UI_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.VUE_APP_DISCOVERY_UI_FIREBASE_DB_URL,
  projectId: process.env.VUE_APP_DISCOVERY_UI_FIREBASE_PROJECT_ID,
};

const app = firebase.initializeApp(config);

class Service {

  public getPeopleStatsByGgId(ggId: string, topicId: string): Observable<IPeopleStats> {
    const path = `k10/v3/stats/${ggId}/unreads/topics/opportunities=${topicId}/people`;
    return this.signIn().pipe(
      flatMap(() => {
        const peopleStatsRef = app.database().ref(path);

        return fromRef(peopleStatsRef, ListenEvent.value)
          .pipe(
            map((change: any) => change.snapshot.val()),
            debounceTime(200)
          );
      })
    );
  }

  public updateBoard(opportunityId: string): Observable<{ boardVersion: number, reason: string }> {
    const boardRef = app.database().ref(`cardinal/boards/${opportunityId}`);

    return fromRef(boardRef, ListenEvent.value)
      .pipe(
        map((change: any) => change.snapshot.val()),
        debounceTime(200)
      );
  }

  public usernameChangedNotification(subjectId: number): Observable<string> {
    const usernameRef = firebase.database().ref(`people/${subjectId}`);

    return fromRef(usernameRef, ListenEvent.value)
      .pipe(
        map((change: any) => {
          const data = change.snapshot.val();
          return !!data ? data.username : "";
        }),
        debounceTime(200)
      );
  }

  public opportunityOrDraftChangedNotification(opportunityId?: string, draftToken?: string): Observable<string> {
    const reference = !!draftToken
      ? firebase.database().ref(`drafts/${draftToken}`)
      : firebase.database().ref(`opportunities/${opportunityId}`);

    return fromRef(reference, ListenEvent.value)
      .pipe(
        map((change: any) => {
          const data = change.snapshot.val();
          return !!data ? data.modifiedBy : "";
        }),
        debounceTime(200)
      );
  }

  public publicStatsRef(ggId: string): firebase.database.Reference {
    return firebase.database().ref(`k10/v3/public-stats/${ggId}`);
  }

  public updateLastActivity(
    ggId: string,
    pageTitle: string,
    pageUrl: string,
    created: string
  ): void {
    const reference = firebase.database().ref(`k10/v3/public-stats/${ggId}/last-web-activity`);
    reference.set({ pageTitle, pageUrl, created });
  }

  public opportunityQuestionsChangedNotification(opportunityId: string): Observable<string> {
    const reference = firebase.database().ref(`opportunity-questions/${opportunityId}`);

    return fromRef(reference, ListenEvent.value)
      .pipe(
        map((change: any) => {
          const data = change.snapshot.val();
          return !!data ? data.modifiedBy : "";
        }),
        debounceTime(200)
      );
  }

  public candidateRanking(ggId: string, opportunityId: string): Observable<CandidateRanking | undefined> {
    const reference = firebase.database().ref(`rankings/people/${ggId}/opportunities/${opportunityId}/ranking`);

    return fromRef(reference, ListenEvent.value)
      .pipe(
        map((change: any) => {
          const data = change.snapshot.val();
          return !!data ? { ...data, opportunityId } : undefined;
        }),
        debounceTime(200)
      );
  }

  public completion(subjectId: number): Observable<{ progress: number, step: number } | undefined> {
    const reference = firebase.database().ref(`people/${subjectId}/stats/completion`);

    return fromRef(reference, ListenEvent.value)
      .pipe(
        map((change: any) => {
          const data = change.snapshot.val();
          return !!data ? data : undefined;
        }),
        debounceTime(200)
      );
  }

  public getFirebaseApp(): firebase.app.App {
    return app;
  }

  public signIn(): Observable<firebase.User> {
    const currentUser = app.auth().currentUser;

    if (currentUser) {
      return of(currentUser);
    } else {
      return UserService.getUserData()
        .pipe(
          flatMap(() => AuthService.firebaseLogin()),
          flatMap((token) => from(app.auth().signInWithCustomToken(token))),
          map((credentials) => credentials.user!!)
        );
    }
  }
}

export const FirebaseService = new Service();
